import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "../components/Dialog"
import SearchProducts from "./SearchProducts"
import { useSubscription } from "../hooks/useSubscription";
import { useEffect, useState } from "react";
import { useGlobal } from "../context/globalProvider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./Tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";

const AddProductsDialog = ({ 
  properties = [],
  chargeDate,
  addressId,
  Trigger,
  defaultTagNames = ['Shop All Stages'],
  title = "Add Products to Your Order",
  description = "Add a new subscription for 15% off every month, or add a one time purchase to your next delivery by selecting a product below.",
  subscriptionOnly = false,
  defaultProducts,
  source = 'customer_portal',
  sale,
  chargeId,
  nextOrder,
  showBanner = false,
}: { 
  properties?: any[],
  chargeDate: string,
  addressId: string,
  Trigger: React.ReactNode,
  defaultTagNames?: string[],
  title?: string,
  description?: string,
  subscriptionOnly?: boolean,
  defaultProducts?: any[],
  source?: string,
  sale?: boolean,
  chargeId?: string,
  nextOrder?: any,
  showBanner?: boolean,
}) => {
  const { createSubscription, createOneTimeOrder, getProductPlans  } = useSubscription();
  const [open, setOpen] = useState(false);
  const { sendEvent } = useGlobal();

  useEffect(() => {
    if (open) {
      sendEvent('add_products_dialog_opened', { source });
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{Trigger}</DialogTrigger>
      <DialogContent className='bg-white md:!max-w-[910px]'>
        {
          showBanner ? (
            <div className="flex items-end md:items-center gap-8 bg-cream relative h-0 pb-[21%] md:pb-[17%] rounded overflow-hidden">
              <img 
                src="https://cdn.shopify.com/s/files/1/0417/3817/4613/files/labour_day_add_products.jpg?v=1787106922&width=2500" 
                alt="Labor Day Add Products Banner" 
                className="absolute inset-0 w-full h-full object-cover hidden md:block object-left" 
                loading="eager" 
              />

              <img 
                src="https://cdn.shopify.com/s/files/1/0417/3817/4613/files/labour_day_add_products_mobile.jpg?v=1787106992&width=1000" 
                alt="Labor Day Add Products Banner" 
                className="absolute inset-0 w-full h-full block md:hidden object-cover object-top" 
                loading="eager" 
              />
              <div className="text-sm absolute z-10 inset-0 flex justify-center px-4 md:pr-0">
                <div className="flex items-center justify-center gap-4">
                  <img 
                    src="https://cdn.shopify.com/s/files/1/0417/3817/4613/files/labor_day_badge_portakl.png?v=1787092313&width=300" 
                    alt="Labor Day Banner Badge"
                    width={180}
                    height={130}
                    className="w-[28%] h-auto" 
                    loading="lazy"
                  />
                  <div className="flex flex-col md:flex-row md:gap-5 md:items-end">
                    <h2 className="text-[clamp(19px,_8.1vw,_61px)] font-semibold leading-none">
                      20% Off
                    </h2>
                    <p className="text-[clamp(9px,_2.9vw,_22px)] font-semibold leading-none translate-y-[-0.26em]">
                      any new <br className="hidden md:block"/>
                      routine builder
                      <Popover modal={true}>
                        <PopoverTrigger className="ml-1">
                          <svg className="inline-block cursor-pointer" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5.41619 3.312C5.41619 3.15733 5.46952 3.02933 5.57619 2.928C5.68285 2.82667 5.82419 2.776 6.00019 2.776C6.17619 2.776 6.31752 2.82667 6.42419 2.928C6.53619 3.02933 6.59219 3.15733 6.59219 3.312C6.59219 3.46667 6.53619 3.592 6.42419 3.688C6.31752 3.784 6.17619 3.832 6.00019 3.832C5.82419 3.832 5.68285 3.784 5.57619 3.688C5.46952 3.592 5.41619 3.46667 5.41619 3.312ZM6.45619 4.592V9H5.54419V4.592H6.45619Z" fill="#000"/>
                            <circle cx="6" cy="6" r="5.375" stroke="#000" strokeWidth="0.75"/>
                          </svg>
                        </PopoverTrigger>
                        <PopoverContent className="!bg-white">
                          <p className="text-xs max-w-[400px] text-black">
                            This offer expires on Tuesday, September 8, 2026 at 11:59pm PT. Save 20% off when you add a new routine builder. Cannot be combined with other offers, discounts or applied to prior purchases. Limited to one use per customer. Offer applicable to routine builders only; excludes gift cards, gift kits, apparel and accessories. Eligible routine builders include: Triple-Support Protein, Daily Resilience Complex, Cellular Hydration Powder, Synbiotic Greens Powder, Sleep Support, Gentle Prenatal Powder, Fertility+ Support, Hormonal Balance Support, Daily Probiotic, Choline Boost, Daily Iron Ease, Libido Support, Stress Support, Omega DHA + EPA. Please note that we limit subscriptions to one unit per customer for any specific product. (That said, feel free to subscribe to multiple products!)
                          </p>
                        </PopoverContent>
                      </Popover>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <DialogTitle>{title} {sale ? (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <svg className="inline" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5.41619 3.312C5.41619 3.15733 5.46952 3.02933 5.57619 2.928C5.68285 2.82667 5.82419 2.776 6.00019 2.776C6.17619 2.776 6.31752 2.82667 6.42419 2.928C6.53619 3.02933 6.59219 3.15733 6.59219 3.312C6.59219 3.46667 6.53619 3.592 6.42419 3.688C6.31752 3.784 6.17619 3.832 6.00019 3.832C5.82419 3.832 5.68285 3.784 5.57619 3.688C5.46952 3.592 5.41619 3.46667 5.41619 3.312ZM6.45619 4.592V9H5.54419V4.592H6.45619Z" fill="#8B8B8B"/>
                      <circle cx="6" cy="6" r="5.375" stroke="#8B8B8B" strokeWidth="0.75"/>
                    </svg>
                  </TooltipTrigger>
                  <TooltipContent className="bg-white!" side="bottom">
                    <p className="text-xs max-w-[400px]">
                      This offer expires on Tuesday, December 2, 2025 at 11:59pm PT. Save 25% off when you subscribe to a new routine builder. Cannot be combined with other offers, discounts or applied to prior purchases. Limited to one use per customer. Offer applied to routine builders only; excludes gift cards, gift kits, apparel and accessories. Supplies are limited. Offer valid while supplies last. Purchases are limited to 1 unit per SKU. Please note that we limit subscriptions to one unit per customer for any specific product. (That said, feel free to subscribe to multiple products!)
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider> 
            ) : null
          }
          </DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
            </>
          )
        }
        
        <SearchProducts
          source={source}
          defaultProducts={defaultProducts}
          subscriptionOnly={subscriptionOnly}
          defaultTagNames={showBanner ? ["Shop All Sale"] : defaultTagNames}
          properties={[{ name: 'added_from', value: 'add_products_dialog' }]}
          // defaultTagNames={["Shop All Sale"]}
          sale={sale}
          onSubmit={async (product) => {
            if (product.has_plans) {
              const plans = await getProductPlans(product.id);
              const subscriptionPlans = plans?.filter((plan) => plan.type === 'subscription').sort((a, b) => a.sort_order - b.sort_order);
              
              const productIsRoutineBuilder = false;
              await createSubscription({
                address_id: Number(addressId),
                quantity: 1,
                external_variant_id: {
                  ecommerce: product.selectedVariant?.id || product.variants[0].id,
                },
                next_charge_scheduled_at: chargeDate,
                plan_id: subscriptionPlans?.[0]?.id,
                properties: [{ name: 'added_from', value: 'add_products_dialog' }],
              }, source, productIsRoutineBuilder ? {
                addDiscountCode: "BYESUMMER",
                chargeCodes: nextOrder?.discounts || []
              } : {})
            } else {
              await createOneTimeOrder({
                product_title: product.title,
                address_id: Number(addressId),
                quantity: 1,
                next_charge_scheduled_at: chargeDate,
                external_variant_id: {
                  ecommerce: product.selectedVariant?.id || product.variants[0].id,
                },
                properties: [{ name: 'added_from', value: 'add_products_dialog' }],
              }, source)
            }

            setOpen(false);
          }}
        />
      </DialogContent>
    </Dialog>
  )
}

export default AddProductsDialog;